/**
 * ── PER-KIND INSTANTIATION (plan §4.1 `instantiate.ts`, §3's "instantiated as" column) ──────────
 *
 * A source is shared and refcounted; an INSTANCE is what a caller parents into the scene. The
 * rule per kind, and the reason each is what it is:
 *
 *  · `mesh`     → `Object3D.clone()` — shares geometry and materials with the source (one upload,
 *                 N draws). If the GLB carries a `SkinnedMesh`, `SkeletonUtils.clone` instead:
 *                 a plain clone of a skinned mesh keeps pointing at the SOURCE's bones, so two
 *                 instances would animate one skeleton.
 *  · `body`     → `SkeletonUtils.clone` always (a body OWNS a skeleton — §5.1) plus the rig
 *                 sidecar, passed through as data.
 *  · `clip`     → the source's `AnimationClip[]`, SHARED. A clip is immutable curve data; a mixer
 *                 binds it per root, so cloning would only double the memory.
 *  · `texture`  → the shared `Texture`. Materials reference it; the refcount is at the source.
 *  · `material` → the shared `SurfaceMaterial` the source already built. See below.
 *
 * ── ⭐ P1.5 · B1 — THE MATERIAL ARM (plan §13.4) ─────────────────────────────────────────────────
 *
 * HARD RULE 7 IS THE WHOLE DESIGN HERE, NOT A CONSTRAINT ON IT. A material asset instantiates
 * through `makeSurface(archetype, params)` and through nothing else: the TSL graphs are built once
 * at module scope in `src/render/materials/surfaceFactory.ts` and every per-instance value rides
 * `materialReference`, which is what keeps N materials on ONE program — `customProgramCacheKey()`
 * hashes node instance ids, so a fresh graph per material is a full WGSL codegen run per material.
 * This module may therefore never construct a node material, never assign a `colorNode`, and never
 * invent an archetype: `MATERIAL_ARCHETYPES` is the factory's own list, mirrored in the pure core
 * and pinned to the factory's source by test.
 *
 * The factory's parameter surface is TYPED (`SurfaceParams`); the catalog's is `Record<string,
 * unknown>`, deliberately — "the factory owns their meaning, not the catalog" (`MaterialDefSchema`).
 * {@link surfaceParamsFrom} is the ONE adapter between them: a key the factory does not have, or a
 * value of the wrong type, is DROPPED with one warning rather than handed to three, because a
 * `roughness: "high"` reaching `MeshStandardNodeMaterial` is a NaN uniform and a black surface with
 * every counter green.
 *
 * A SLOT WHOSE TEXTURE DID NOT ARRIVE IS SIMPLY NOT BOUND (rule 6). The archetype's own default
 * then stands: an unbound `map` on `surf.opaque` is the flat `color`, an unbound `normalMap` is a
 * flat surface. That is a picture, not an error signal — the magenta placeholder is the MESH kind's
 * policy and must not leak here, because a material with no map still shades correctly, and a
 * caller who cannot tell "the texture is late" from "the artist authored no texture" learns nothing
 * from a violent colour. What the degradation IS reported through is `MaterialSource.degraded` plus
 * one warning, which is a state a test can read.
 */

import { clone as skeletonClone } from 'three/addons/utils/SkeletonUtils.js';

import {
  makeSurface,
  type SurfaceMaterial,
  type SurfaceParams,
  type SurfaceTextures,
} from '../render/materials/surfaceFactory';
import type { AssetKind, ResolvedMaterial } from './core/catalog';
import type {
  AssetSource,
  BodyInstance,
  BodySource,
  ClipSource,
  EntityPlacement,
  EntitySource,
  Instance,
  MaterialSource,
  MeshSource,
  SourceOf,
  TextureSource,
} from './handle';
import type * as THREE from 'three/webgpu';

export function instantiateMesh(src: MeshSource): THREE.Object3D {
  return src.skinned ? skeletonClone(src.scene) : src.scene.clone();
}

export function instantiateBody(src: BodySource): BodyInstance {
  return { root: skeletonClone(src.scene), rig: src.rig };
}

export function instantiateClip(src: ClipSource): readonly THREE.AnimationClip[] {
  return src.clips;
}

export function instantiateTexture(src: TextureSource): THREE.Texture {
  return src.texture;
}

/** SHARED, like a texture: one material object, N meshes, one program (see the header). */
export function instantiateMaterial(src: MaterialSource): SurfaceMaterial {
  return src.material;
}

/**
 * ⭐ AN ENTITY INSTANTIATES TO A PLACEMENT, NOT TO AN OBJECT (plan §13.6).
 *
 * `src/assets/**` may not import `src/entities/**` (ADR-0001, `tests/entityBoundary.test.ts`), so
 * this module CANNOT build an `EntityInstance` and must not try. What it builds is the one thing
 * both sides already speak: a world-placement row, `{ type, …params }` — exactly what
 * `world.entities[]` carries and what `EntityHost.spawn` validates, lazy-loads and constructs. The
 * app layer does the spawning; the library only ever says what to spawn.
 *
 * A FRESH object per call, like a mesh clone and unlike a texture: a caller that poses this
 * placement (the summon writes a `pos` into it) must not be writing into the shared source, and
 * two summons of one prefab must not share one def object.
 *
 * ⚠️ `type` IS ASSIGNED LAST, so a params block carrying its own `type` cannot smuggle a different
 * class past `requires.classes` — the same guard `world/prefabs.resolveEntityDef` applies to a
 * world prefab's params, for the same reason.
 */
export function instantiateEntity(src: EntitySource): EntityPlacement {
  return { ...src.params, type: src.class };
}

// ── The catalog → factory adapter ──────────────────────────────────────────────────────────────

/**
 * The factory's texture slots — the keys of `SurfaceTextures`. A material asset's `textures` map is
 * keyed by these; `MATERIAL_SLOT_PATTERN` only says "camelCase", because the catalog does not know
 * what a slot MEANS and must not pretend to.
 *
 * ⚠️ A MIRROR of `surfaceFactory.SurfaceTextures`, pinned to its source by
 * `tests/assets/materialInstance.test.ts` — the same treatment `MATERIAL_ARCHETYPES` gets in the
 * pure core, and for the same reason: this list may not drift from the interface it names.
 */
export const SURFACE_TEXTURE_SLOTS = ['map', 'normalMap', 'orm', 'emissiveMap', 'mask'] as const;
export type SurfaceTextureSlot = (typeof SURFACE_TEXTURE_SLOTS)[number];

const isSlot = (key: string): key is SurfaceTextureSlot =>
  (SURFACE_TEXTURE_SLOTS as readonly string[]).includes(key);

/** How each `SurfaceParams` key is checked before it is allowed to reach the factory. */
const PARAM_KINDS = {
  color: 'colour',
  roughness: 'number',
  metalness: 'number',
  emissive: 'colour',
  emissiveIntensity: 'number',
  alphaTest: 'number',
  side: 'number',
  tint: 'colour',
  maskedRGB: 'object',
} as const satisfies Readonly<Record<string, 'number' | 'colour' | 'object'>>;

/** The `SurfaceParams` keys a catalog may set. `textures` is NOT one — those arrive as deps. */
export const MATERIAL_PARAM_KEYS: readonly string[] = Object.keys(PARAM_KINDS);

function accepts(kind: 'number' | 'colour' | 'object', value: unknown): boolean {
  if (kind === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (kind === 'colour') return (typeof value === 'number' && Number.isFinite(value)) || typeof value === 'string';
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Catalog params → `SurfaceParams`. An unknown key or a wrong-typed value is dropped with ONE
 * warning naming the asset and the key — never coerced and never passed through (see the header).
 */
export function surfaceParamsFrom(
  id: string,
  params: Readonly<Record<string, unknown>>,
  warnOnce: (key: string, message: string) => void,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const kinds = PARAM_KINDS as Readonly<Record<string, 'number' | 'colour' | 'object' | undefined>>;
  for (const [key, value] of Object.entries(params)) {
    const kind = kinds[key];
    if (kind === undefined) {
      warnOnce(
        `material-param:${id}:${key}`,
        `[assets] material '${id}' sets param '${key}', which surfaceFactory has no field for — ignored ` +
          `(the factory's params are ${MATERIAL_PARAM_KEYS.join(', ')})`,
      );
      continue;
    }
    if (!accepts(kind, value)) {
      warnOnce(
        `material-param-type:${id}:${key}`,
        `[assets] material '${id}' sets param '${key}' to a ${typeof value}, which is not a ${kind} — ignored`,
      );
      continue;
    }
    out[key] = value;
  }
  // A `Record<string, unknown>` rather than a `SurfaceParams`, deliberately: every key in it has
  // been checked against `PARAM_KINDS`, and this is also what `MaterialSource.params` publishes —
  // one shape for the factory call and for the state a test reads.
  return out;
}

export interface MaterialBuildInput {
  /** The asset the chain was folded FOR — named in every warning. */
  id: string;
  /** `resolveMaterial(catalog, id)` / `foldMaterialChain(lookup, id)` — the fold, never a second walk. */
  resolved: ResolvedMaterial;
  /** Factory slot → the decoded `Texture` for that slot's asset. A slot absent here DEGRADES. */
  textures: Readonly<Record<string, THREE.Texture>>;
  warnOnce: (key: string, message: string) => void;
}

/**
 * Build the ONE material for an asset. `null` — never a throw — when the chain names no archetype
 * anywhere: there is then nothing for the factory to build, so the service settles the handle
 * `failed('unsupported')` and the caller's placeholder policy is what shows (rule 6, §4.2).
 */
export function buildMaterialSource(input: MaterialBuildInput): MaterialSource | null {
  const { id, resolved, textures, warnOnce } = input;
  if (resolved.archetype === null) return null;
  const bound: string[] = [];
  const degraded: string[] = [];
  const tex: SurfaceTextures = {};
  for (const slot of Object.keys(resolved.textures).sort()) {
    if (!isSlot(slot)) {
      warnOnce(
        `material-slot:${id}:${slot}`,
        `[assets] material '${id}' names texture slot '${slot}', which surfaceFactory has no slot for — ` +
          `ignored (the slots are ${SURFACE_TEXTURE_SLOTS.join(', ')})`,
      );
      continue;
    }
    const texture = textures[slot];
    if (texture === undefined) {
      degraded.push(slot);
      continue;
    }
    tex[slot] = texture;
    bound.push(slot);
  }
  const params = surfaceParamsFrom(id, resolved.params, warnOnce);
  // THE ONE CONSTRUCTION SITE (rule 7). Module-scope graphs, per-instance values through
  // materialReference; this module never touches a node.
  const material = makeSurface(resolved.archetype, { ...(params as SurfaceParams), textures: tex });
  material.name = `${resolved.archetype}:${id}`;
  return {
    kind: 'material',
    archetype: resolved.archetype,
    params,
    slots: { ...resolved.textures },
    bound,
    degraded,
    chain: resolved.chain,
    material,
  };
}

/** The one dispatch. Typed per kind so `acquire(id, { kind: 'body' }).instance()` is a `BodyInstance`. */
export function instantiate<K extends AssetKind>(source: SourceOf<K>): Instance<K> {
  const s = source as AssetSource;
  switch (s.kind) {
    case 'mesh':
      return instantiateMesh(s) as Instance<K>;
    case 'body':
      return instantiateBody(s) as Instance<K>;
    case 'clip':
      return instantiateClip(s) as Instance<K>;
    case 'texture':
      return instantiateTexture(s) as Instance<K>;
    case 'material':
      return instantiateMaterial(s) as Instance<K>;
    case 'entity':
      return instantiateEntity(s) as Instance<K>;
  }
}
